import { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import {
  deleteContenido,
  listContenidos,
  updateContenido,
} from "../api/contenidoApi";
import {
  archiveEvaluacion,
  deleteEvaluacion,
  listEvaluaciones,
} from "../api/evaluacionesApi";
import {
  readContenidoFilters,
  saveContenidoFilters,
} from "../utils/contenidoFiltersStorage";
import ContenidoHeader from "../components/ContenidoHeader";
import ContenidoFilters from "../components/ContenidoFilters";
import ContenidoTable from "../components/ContenidoTable";
import ContenidoScopeModal from "../components/ContenidoScopeModal";
import { CATEGORY_OPTIONS, STATUS_OPTIONS } from "../constants/contentOptions";

function sortByUpdatedAtDesc(a, b) {
  return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
}

export default function ContenidoPage() {
  const navigate = useNavigate();

  const [categoryFilter, setCategoryFilter] = useState(
    () => readContenidoFilters().categoryFilter
  );
  const [statusFilter, setStatusFilter] = useState(
    () => readContenidoFilters().statusFilter
  );
  const [searchQuery, setSearchQuery] = useState(
    () => readContenidoFilters().searchQuery
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeRow, setScopeRow] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    saveContenidoFilters({
      categoryFilter,
      statusFilter,
      searchQuery,
    });
  }, [categoryFilter, statusFilter, searchQuery]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const shouldFetchContents =
          categoryFilter === "ALL" || categoryFilter !== "EVALUACION";

        const shouldFetchEvaluations =
          categoryFilter === "ALL" || categoryFilter === "EVALUACION";

        const [contentsData, evaluationsData] = await Promise.all([
          shouldFetchContents
            ? listContenidos({
                page: 1,
                limit: 100,
                category:
                  categoryFilter === "ALL" || categoryFilter === "EVALUACION"
                    ? undefined
                    : categoryFilter,
                status: statusFilter === "ALL" ? undefined : statusFilter,
                search: debouncedSearchQuery.trim() || undefined,
              })
            : Promise.resolve({ items: [] }),

          shouldFetchEvaluations
            ? listEvaluaciones({
                page: 1,
                limit: 100,
                status: statusFilter === "ALL" ? undefined : statusFilter,
                search: debouncedSearchQuery.trim() || undefined,
              })
            : Promise.resolve({ items: [] }),
        ]);

        if (!alive) return;

        const contentRows = Array.isArray(contentsData?.items)
          ? contentsData.items.map((item) => ({
              ...item,
              itemType: "CONTENT",
              rowKey: `CONTENT-${item.id}`,
            }))
          : [];

        const evaluationRows = Array.isArray(evaluationsData?.items)
          ? evaluationsData.items.map((item) => ({
              ...item,
              itemType: "EVALUATION",
              category: "EVALUACION",
              rowKey: `EVALUATION-${item.id}`,
            }))
          : [];

        setRows([...contentRows, ...evaluationRows].sort(sortByUpdatedAtDesc));
        setError("");
      } catch (err) {
        if (!alive) return;
            
        setError(mapApiError(err).message);
      } finally {
        if (!alive) return;
        setLoading(false);
        setHasLoadedOnce(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [categoryFilter, statusFilter, debouncedSearchQuery, reloadKey]);

  const handleOpenScope = (row) => {
    if (row.itemType !== "CONTENT") return;

    setScopeRow(row);
    setScopeOpen(true);
  };

  const handleCloseScope = () => {
    setScopeOpen(false);
    setScopeRow(null);
  };

  const handleEdit = (row) => {
    if (row.itemType === "EVALUATION") {
      navigate(`/rrhh/contenido/evaluaciones/${row.id}/editar`);
      return;
    }

    navigate(`/rrhh/contenido/${row.id}/editar`);
  };

  const handleResults = (row) => {
    navigate(`/rrhh/contenido/evaluaciones/${row.id}/resultados`);
  };

  const handleArchive = async (row) => {
    try {
      if (row.itemType === "EVALUATION") {
        await archiveEvaluacion(row.id);
      } else {
        await updateContenido(row.id, { status: "ARCHIVED" });
      }

      setReloadKey((value) => value + 1);
    } catch (err) {
      window.alert(mapApiError(err).message);
    }
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`¿Seguro que querés borrar "${row.title}"?`);
    if (!ok) return;

    try {
      if (row.itemType === "EVALUATION") {
        await deleteEvaluacion(row.id);
      } else {
        await deleteContenido(row.id);
      }

      setReloadKey((value) => value + 1);
    } catch (err) {
      window.alert(mapApiError(err).message);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <ContenidoHeader title="Contenido" onBack={() => navigate("/rrhh/dashboard")} />

      <ContenidoFilters
        categoryFilter={categoryFilter}
        statusFilter={statusFilter}
        searchQuery={searchQuery}
        categoryOptions={CATEGORY_OPTIONS}
        statusOptions={STATUS_OPTIONS}
        onCategoryChange={(event) => setCategoryFilter(event.target.value)}
        onStatusChange={(event) => setStatusFilter(event.target.value)}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        onCreateContent={() => navigate("/rrhh/contenido/crear")}
        onCreateEvaluation={() => navigate("/rrhh/contenido/evaluaciones/crear")}
      />

      {!!error && (
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "error.main" }}>
          {error}
        </Typography>
      )}

      <ContenidoTable
        rows={rows}
        loading={!hasLoadedOnce && loading}
        onEdit={handleEdit}
        onScope={handleOpenScope}
        onResults={handleResults}
        onArchive={handleArchive}
        onDelete={handleDelete}
      />

      <ContenidoScopeModal
        open={scopeOpen}
        contentId={scopeRow?.id ?? null}
        title={scopeRow?.title ?? ""}
        onClose={handleCloseScope}
      />
    </Stack>
  );
}